import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { oggiRoma, aggiungiGiorniIso, formatDataOraRoma } from '../../../utils/dateRoma';
import { API_DOCS } from './AgendaVenditoriPage.api';
import { AppuntamentoAgenda, BADGE_STATO_APPUNTAMENTO, ETICHETTA_STATO_APPUNTAMENTO, ETICHETTA_ORIGINE } from './percorsoTipi';

interface DatiAgenda {
  appuntamenti: AppuntamentoAgenda[];
  venditori: { id: number; nome: string }[];
  sedi: { id: number; nome: string }[];
}

export default function AgendaVenditoriPage() {
  const navigate = useNavigate();
  const { societaId, pronta } = useFiltroSocieta();
  const { utente } = usePermesso();
  const [da, setDa] = useState(oggiRoma());
  const [a, setA] = useState(aggiungiGiorniIso(oggiRoma(), 13));
  const [sedeId, setSedeId] = useState('');
  const [venditoreId, setVenditoreId] = useState('');
  const [annullati, setAnnullati] = useState(false);
  const [dati, setDati] = useState<DatiAgenda | null>(null);
  const [errore, setErrore] = useState('');
  // Ref e non state: cambiarlo non deve far ripartire il caricamento.
  const primoCaricamento = useRef(true);

  // Negozi e venditori dipendono dalla società: cambiandola, i filtri scelti per l'altra non valgono più.
  useEffect(() => {
    setSedeId('');
    setVenditoreId('');
  }, [societaId]);

  useEffect(() => {
    if (!pronta) return;
    // Risposte fuori ordine (filtri cambiati mentre la richiesta è in corso): vale solo l'ultima.
    let attiva = true;
    const qs = new URLSearchParams({ da, a });
    if (societaId) qs.set('societaId', societaId);
    if (sedeId) qs.set('sedeId', sedeId);
    if (venditoreId) qs.set('venditoreId', venditoreId);
    if (annullati) qs.set('annullati', '1');
    setErrore('');
    // @api GET /api/percorso-acquirenti/agenda?societaId&sedeId&venditoreId&da&a&annullati -> {"success":true,"data":{"appuntamenti":"array","venditori":"array","sedi":"array","da":"string","a":"string"}}
    apiFetch<{ success: boolean; data: DatiAgenda }>(`/api/percorso-acquirenti/agenda?${qs}`)
      .then(r => {
        if (!attiva) return;
        setDati(r.data);
        // Al primo caricamento un venditore vede subito i suoi appuntamenti.
        if (primoCaricamento.current && utente && r.data.venditori.some(v => v.id === utente.id)) setVenditoreId(String(utente.id));
        primoCaricamento.current = false;
      })
      .catch(e => { if (attiva) setErrore(e.message); });
    return () => { attiva = false; };
  }, [pronta, societaId, da, a, sedeId, venditoreId, annullati, utente]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h1 className="bop-page-title">Agenda venditori</h1>
          <p className="bop-page-subtitle">Appuntamenti degli acquirenti prenotati dalla pagina del cantiere o dal negozio</p>
        </div>
      </div>

      <div className="bop-filter-bar">
        <input className="bop-input" type="date" value={da} onChange={e => setDa(e.target.value)} aria-label="Dal" />
        <input className="bop-input" type="date" value={a} onChange={e => setA(e.target.value)} aria-label="Al" />
        <select className="bop-select" value={sedeId} onChange={e => setSedeId(e.target.value)} aria-label="Negozio">
          <option value="">Tutti i negozi</option>
          {dati?.sedi.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <select className="bop-select" value={venditoreId} onChange={e => setVenditoreId(e.target.value)} aria-label="Venditore">
          <option value="">Tutti i venditori</option>
          {dati?.venditori.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
        </select>
        <label className="bop-percorso__riga">
          <input type="checkbox" checked={annullati} onChange={e => setAnnullati(e.target.checked)} /> Mostra annullati
        </label>
      </div>

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      <div className="bop-card">
        <table className="bop-table bop-table--clickable">
          <thead>
            <tr>
              <th>Quando</th><th>Acquirente</th><th>Cantiere</th><th>Negozio</th><th>Venditore</th><th>Incontro</th><th>Origine</th><th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {!dati || dati.appuntamenti.length === 0 ? (
              <tr><td colSpan={8}><div className="bop-empty-state">{dati ? 'Nessun appuntamento nel periodo' : 'Caricamento…'}</div></td></tr>
            ) : dati.appuntamenti.map(app => (
              <tr key={app.id} onClick={() => navigate(`/commerciale/percorso/acquirenti/${app.acquirente.id}`)}>
                <td>{formatDataOraRoma(app.inizio)}</td>
                <td>
                  {app.acquirente.cognome} {app.acquirente.nome}
                  <br /><span className="bop-text-muted">Unità {app.acquirente.unitaCodice}{app.acquirente.telefono ? ` · ${app.acquirente.telefono}` : ''}</span>
                </td>
                <td>{app.commessa.offertaCapitolato?.oggetto || app.commessa.numero}</td>
                <td>{app.commessa.sede.nome}</td>
                <td>{app.venditore.nome}</td>
                <td>{app.numeroIncontro}</td>
                <td>{ETICHETTA_ORIGINE[app.origine]}</td>
                <td><span className={`bop-badge ${BADGE_STATO_APPUNTAMENTO[app.stato]}`}>{ETICHETTA_STATO_APPUNTAMENTO[app.stato]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
